export type OracleFunctionDoc = {
  name: string;
  signature?: string;
  description: string;
};

export const ORACLE_KEYWORDS: readonly string[] = [
  // DML
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'CROSS', 'FULL', 'OUTER', 'NATURAL',
  'ON', 'USING', 'GROUP', 'BY', 'HAVING', 'ORDER', 'ASC', 'DESC',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE', 'MERGE',
  'FETCH', 'FIRST', 'ROWS', 'ONLY', 'NEXT', 'PERCENT', 'WITH', 'TIES', 'OFFSET',
  // DDL
  'CREATE', 'TABLE', 'VIEW', 'INDEX', 'SEQUENCE', 'SYNONYM', 'TYPE',
  'MATERIALIZED', 'DATABASE', 'LINK', 'DIRECTORY', 'TABLESPACE',
  'DROP', 'ALTER', 'RENAME', 'TRUNCATE', 'COMMENT',
  // Query structure
  'AS', 'DISTINCT', 'UNION', 'ALL', 'INTERSECT', 'MINUS', 'EXISTS', 'IN',
  'LIKE', 'BETWEEN', 'IS', 'NULL', 'NOT', 'AND', 'OR',
  'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  // PL/SQL
  'DECLARE', 'BEGIN', 'EXCEPTION', 'CURSOR', 'LOOP', 'FOR', 'WHILE',
  'IF', 'ELSIF', 'RETURN', 'RAISE', 'RAISE_APPLICATION_ERROR',
  'BULK', 'COLLECT', 'FORALL', 'PRAGMA', 'RECORD',
  'REF', 'OPEN', 'CLOSE', 'EXIT', 'CONTINUE',
  'EXECUTE', 'IMMEDIATE',
  // Constraints & keys
  'PRIMARY', 'KEY', 'FOREIGN', 'REFERENCES', 'UNIQUE', 'CHECK', 'DEFAULT', 'CONSTRAINT',
  'CASCADE', 'RESTRICT', 'ENABLE', 'DISABLE', 'VALIDATE', 'NOVALIDATE',
  // Transactions
  'COMMIT', 'ROLLBACK', 'SAVEPOINT',
  // Privileges
  'GRANT', 'REVOKE', 'TO', 'PUBLIC', 'WITH', 'ADMIN', 'OPTION',
  // Oracle-specific
  'DUAL', 'ROWNUM', 'ROWID', 'LEVEL', 'CONNECT', 'START', 'PRIOR',
  'NOCACHE', 'CACHE', 'CYCLE', 'NOCYCLE', 'INCREMENT', 'MINVALUE', 'MAXVALUE',
  'NEXTVAL', 'CURRVAL',
  'PARTITION', 'SUBPARTITION', 'RANGE', 'LIST', 'HASH',
  'EXPLAIN', 'PLAN',
  // Package/procedure
  'PACKAGE', 'BODY', 'PROCEDURE', 'FUNCTION', 'TRIGGER',
  'REPLACE', 'FORCE', 'EDITIONABLE', 'NONEDITIONABLE',
  // Oracle 23ai / 26ai — accepted only by 23ai-family servers (19c rejects them)
  'QUALIFY', 'VECTOR', 'ASSERTION',
] as const;

const KEYWORD_DOCS: Record<string, string> = {
  SELECT: 'Return rows from a query.',
  FROM: 'Specify the source table(s) or subquery.',
  WHERE: 'Filter rows by a condition.',
  JOIN: 'Combine rows from multiple tables.',
  GROUP: 'Group rows for aggregate functions.',
  ORDER: 'Sort result rows.',
  INSERT: 'Insert new rows into a table.',
  UPDATE: 'Update existing rows in a table.',
  DELETE: 'Delete rows from a table.',
  MERGE: 'Conditionally insert, update, or delete rows (UPSERT).',
  CREATE: 'Create a database object (table, view, index, …).',
  TABLE: 'A table object.',
  VIEW: 'A stored query (view).',
  SEQUENCE: 'A sequence generator for unique numbers.',
  SYNONYM: 'An alias for another database object.',
  PACKAGE: 'A PL/SQL package containing procedures and functions.',
  PROCEDURE: 'A stored procedure.',
  FUNCTION: 'A stored function.',
  TRIGGER: 'A database trigger.',
  EXPLAIN: 'Show query execution plan.',
  GRANT: 'Grant privileges to a user or role.',
  REVOKE: 'Revoke privileges from a user or role.',
  DECLARE: 'Begin a PL/SQL declaration section.',
  BEGIN: 'Begin a PL/SQL execution block.',
  EXCEPTION: 'Handle errors in a PL/SQL block.',
  DUAL: 'Single-row, single-column table for computations.',
  ROWNUM: 'Pseudo-column for row numbering in result sets.',
  FETCH: 'Limit rows returned (FETCH FIRST N ROWS ONLY).',
  COMMIT: 'Make transaction changes permanent.',
  ROLLBACK: 'Undo transaction changes.',
  QUALIFY: 'Filter on analytic (window) function results — like HAVING for OVER(). Oracle 26ai.',
  VECTOR: 'AI Vector Search: VECTOR(dims, format) columns and CREATE VECTOR INDEX. Oracle 23ai+.',
  ASSERTION: 'CREATE ASSERTION — declarative cross-table integrity constraint. Oracle 26ai.',
};

export const ORACLE_FUNCTIONS: readonly OracleFunctionDoc[] = [
  // Aggregate
  { name: 'COUNT', signature: 'COUNT(expr)', description: 'Count rows or non-NULL values.' },
  { name: 'SUM', signature: 'SUM(expr)', description: 'Sum numeric values.' },
  { name: 'AVG', signature: 'AVG(expr)', description: 'Average numeric values.' },
  { name: 'MIN', signature: 'MIN(expr)', description: 'Minimum value.' },
  { name: 'MAX', signature: 'MAX(expr)', description: 'Maximum value.' },
  { name: 'LISTAGG', signature: 'LISTAGG(expr, delimiter) WITHIN GROUP (ORDER BY ...)', description: 'Concatenate values into a delimited string.' },
  // Null handling
  { name: 'NVL', signature: 'NVL(expr1, expr2)', description: 'Return expr2 if expr1 is NULL.' },
  { name: 'NVL2', signature: 'NVL2(expr1, expr2, expr3)', description: 'Return expr2 if expr1 is not NULL, else expr3.' },
  { name: 'COALESCE', signature: 'COALESCE(expr1, expr2, ...)', description: 'First non-NULL argument.' },
  { name: 'NULLIF', signature: 'NULLIF(expr1, expr2)', description: 'NULL if expr1 equals expr2.' },
  { name: 'DECODE', signature: 'DECODE(expr, search, result [, search, result]... [, default])', description: 'Conditional value mapping (Oracle-specific IF-THEN-ELSE).' },
  // Conversion
  { name: 'TO_CHAR', signature: 'TO_CHAR(expr [, format])', description: 'Convert to character string.' },
  { name: 'TO_NUMBER', signature: 'TO_NUMBER(expr [, format])', description: 'Convert to number.' },
  { name: 'TO_DATE', signature: 'TO_DATE(string, format)', description: 'Convert string to date.' },
  { name: 'TO_TIMESTAMP', signature: 'TO_TIMESTAMP(string, format)', description: 'Convert string to timestamp.' },
  { name: 'CAST', signature: 'CAST(expr AS type)', description: 'Convert value to specified type.' },
  // String
  { name: 'SUBSTR', signature: 'SUBSTR(string, position [, length])', description: 'Extract substring.' },
  { name: 'INSTR', signature: 'INSTR(string, substring [, position [, occurrence]])', description: 'Find position of substring.' },
  { name: 'LENGTH', signature: 'LENGTH(string)', description: 'Length of string in characters.' },
  { name: 'REPLACE', signature: 'REPLACE(string, search [, replacement])', description: 'Replace occurrences of a substring.' },
  { name: 'TRIM', signature: 'TRIM([LEADING|TRAILING|BOTH] char FROM string)', description: 'Remove leading/trailing characters.' },
  { name: 'UPPER', signature: 'UPPER(string)', description: 'Convert to uppercase.' },
  { name: 'LOWER', signature: 'LOWER(string)', description: 'Convert to lowercase.' },
  { name: 'INITCAP', signature: 'INITCAP(string)', description: 'Capitalize first letter of each word.' },
  { name: 'LPAD', signature: 'LPAD(string, length [, pad])', description: 'Left-pad string to specified length.' },
  { name: 'RPAD', signature: 'RPAD(string, length [, pad])', description: 'Right-pad string to specified length.' },
  { name: 'CONCAT', signature: 'CONCAT(string1, string2)', description: 'Concatenate two strings.' },
  // Numeric
  { name: 'TRUNC', signature: 'TRUNC(number [, decimals])', description: 'Truncate to specified decimal places.' },
  { name: 'ROUND', signature: 'ROUND(number [, decimals])', description: 'Round to specified decimal places.' },
  { name: 'CEIL', signature: 'CEIL(number)', description: 'Smallest integer >= number.' },
  { name: 'FLOOR', signature: 'FLOOR(number)', description: 'Largest integer <= number.' },
  { name: 'MOD', signature: 'MOD(m, n)', description: 'Remainder of m divided by n.' },
  { name: 'ABS', signature: 'ABS(number)', description: 'Absolute value.' },
  { name: 'SIGN', signature: 'SIGN(number)', description: 'Sign of number (-1, 0, or 1).' },
  { name: 'POWER', signature: 'POWER(base, exponent)', description: 'Raise base to exponent.' },
  { name: 'SQRT', signature: 'SQRT(number)', description: 'Square root.' },
  // Date/time
  { name: 'SYSDATE', signature: 'SYSDATE', description: 'Current date and time of the database server.' },
  { name: 'SYSTIMESTAMP', signature: 'SYSTIMESTAMP', description: 'Current timestamp with time zone.' },
  { name: 'CURRENT_DATE', signature: 'CURRENT_DATE', description: 'Current date in session time zone.' },
  { name: 'CURRENT_TIMESTAMP', signature: 'CURRENT_TIMESTAMP', description: 'Current timestamp in session time zone.' },
  { name: 'ADD_MONTHS', signature: 'ADD_MONTHS(date, months)', description: 'Add months to a date.' },
  { name: 'MONTHS_BETWEEN', signature: 'MONTHS_BETWEEN(date1, date2)', description: 'Number of months between two dates.' },
  { name: 'LAST_DAY', signature: 'LAST_DAY(date)', description: 'Last day of the month containing date.' },
  { name: 'NEXT_DAY', signature: 'NEXT_DAY(date, day_of_week)', description: 'Next occurrence of specified weekday after date.' },
  { name: 'EXTRACT', signature: 'EXTRACT(part FROM expr)', description: 'Extract year, month, day, etc. from datetime.' },
  // Regex
  { name: 'REGEXP_LIKE', signature: 'REGEXP_LIKE(source, pattern [, match_param])', description: 'Test if string matches a regular expression.' },
  { name: 'REGEXP_SUBSTR', signature: 'REGEXP_SUBSTR(source, pattern [, position [, occurrence [, match_param]]])', description: 'Extract substring matching a regular expression.' },
  { name: 'REGEXP_REPLACE', signature: 'REGEXP_REPLACE(source, pattern [, replacement [, position [, occurrence [, match_param]]]])', description: 'Replace substrings matching a regular expression.' },
  { name: 'REGEXP_INSTR', signature: 'REGEXP_INSTR(source, pattern [, position [, occurrence [, return_opt [, match_param]]]])', description: 'Position of a regular expression match.' },
  { name: 'REGEXP_COUNT', signature: 'REGEXP_COUNT(source, pattern [, position [, match_param]])', description: 'Count occurrences of a regular expression pattern.' },
  // Window/analytic
  { name: 'ROW_NUMBER', signature: 'ROW_NUMBER() OVER (...)', description: 'Sequential row number in result set.' },
  { name: 'RANK', signature: 'RANK() OVER (...)', description: 'Rank with gaps for ties.' },
  { name: 'DENSE_RANK', signature: 'DENSE_RANK() OVER (...)', description: 'Rank without gaps for ties.' },
  { name: 'LEAD', signature: 'LEAD(expr [, offset [, default]]) OVER (...)', description: 'Access a following row value.' },
  { name: 'LAG', signature: 'LAG(expr [, offset [, default]]) OVER (...)', description: 'Access a preceding row value.' },
  { name: 'FIRST_VALUE', signature: 'FIRST_VALUE(expr) OVER (...)', description: 'First value in an ordered set.' },
  { name: 'LAST_VALUE', signature: 'LAST_VALUE(expr) OVER (...)', description: 'Last value in an ordered set.' },
  { name: 'NTILE', signature: 'NTILE(n) OVER (...)', description: 'Divide rows into n roughly equal groups.' },
  // JSON (Oracle 12c+)
  { name: 'JSON_VALUE', signature: 'JSON_VALUE(json_doc, json_path)', description: 'Extract a scalar value from JSON.' },
  { name: 'JSON_QUERY', signature: 'JSON_QUERY(json_doc, json_path)', description: 'Extract a JSON object or array.' },
  { name: 'JSON_TABLE', signature: 'JSON_TABLE(json_doc, json_path COLUMNS (...))', description: 'Create a relational view of JSON data.' },
  // XML
  { name: 'XMLAGG', signature: 'XMLAGG(xml_element ORDER BY ...)', description: 'Aggregate XML fragments.' },
  // AI Vector Search (Oracle 23ai+) — rejected by 19c and earlier
  { name: 'VECTOR_DISTANCE', signature: 'VECTOR_DISTANCE(vector1, vector2 [, metric])', description: 'Distance between two vectors (COSINE default; DOT, EUCLIDEAN, MANHATTAN, HAMMING, …). Oracle 23ai+.' },
  { name: 'TO_VECTOR', signature: 'TO_VECTOR(expr [, dimensions [, format]])', description: 'Convert a string/array expression to a VECTOR value. Oracle 23ai+.' },
  { name: 'VECTOR_SERIALIZE', signature: 'VECTOR_SERIALIZE(vector [RETURNING type])', description: 'Convert a VECTOR to its textual form. Oracle 23ai+.' },
  { name: 'VECTOR_EMBEDDING', signature: 'VECTOR_EMBEDDING(model USING expr)', description: 'Generate an embedding with an in-database ONNX model. Oracle 23ai+.' },
  // Date arithmetic (Oracle 26ai) — SQL Server/MySQL-style helpers
  { name: 'DATEDIFF', signature: 'DATEDIFF(unit, start, end)', description: 'Difference between two datetimes in the given unit (YEAR, MONTH, DAY, …). Oracle 26ai.' },
  { name: 'DATEADD', signature: 'DATEADD(unit, amount, datetime)', description: 'Add an interval to a datetime (SQL Server-style). Oracle 26ai.' },
] as const;

export const ORACLE_DATA_DICTIONARY_VIEWS: readonly string[] = [
  'ALL_TABLES', 'ALL_TAB_COLUMNS', 'ALL_VIEWS', 'ALL_OBJECTS', 'ALL_SEQUENCES',
  'ALL_SYNONYMS', 'ALL_PROCEDURES', 'ALL_SOURCE', 'ALL_CONSTRAINTS',
  'ALL_CONS_COLUMNS', 'ALL_INDEXES', 'ALL_IND_COLUMNS', 'ALL_TRIGGERS',
  'ALL_DEPENDENCIES', 'ALL_DB_LINKS', 'ALL_DIRECTORIES', 'ALL_TYPES',
  'ALL_TYPE_ATTRS', 'ALL_TYPE_METHODS', 'ALL_TAB_COMMENTS', 'ALL_COL_COMMENTS',
  'ALL_MVIEWS', 'ALL_MVIEW_LOGS', 'ALL_USERS', 'ALL_TAB_PRIVS',
  'DBA_TABLES', 'DBA_TAB_COLUMNS', 'DBA_VIEWS', 'DBA_OBJECTS', 'DBA_SEQUENCES',
  'DBA_SYNONYMS', 'DBA_PROCEDURES', 'DBA_SOURCE', 'DBA_CONSTRAINTS',
  'DBA_DATA_FILES', 'DBA_TABLESPACES', 'DBA_USERS', 'DBA_ROLES',
  'DBA_FREE_SPACE', 'DBA_SEGMENTS', 'DBA_EXTENTS',
  'USER_TABLES', 'USER_TAB_COLUMNS', 'USER_VIEWS', 'USER_OBJECTS', 'USER_SEQUENCES',
  'USER_SYNONYMS', 'USER_PROCEDURES', 'USER_SOURCE', 'USER_CONSTRAINTS',
  // SQL/PGQ (property graph) catalog views — Oracle 23ai+ (O2 / Task 20).
  'ALL_PROPERTY_GRAPHS', 'USER_PROPERTY_GRAPHS', 'DBA_PROPERTY_GRAPHS',
  'ALL_PG_ELEMENTS', 'USER_PG_ELEMENTS',
  'ALL_PG_ELEMENT_LABELS', 'USER_PG_ELEMENT_LABELS',
  'ALL_PG_LABELS', 'USER_PG_LABELS',
  'ALL_PG_LABEL_PROPERTIES', 'USER_PG_LABEL_PROPERTIES',
  'ALL_PG_PROP_DEFINITIONS', 'USER_PG_PROP_DEFINITIONS',
  'ALL_PG_KEYS', 'USER_PG_KEYS',
] as const;

export function getOracleKeywordDoc(keyword: string): string | undefined {
  return KEYWORD_DOCS[keyword.toUpperCase()];
}

export function getOracleFunctionDoc(name: string): OracleFunctionDoc | undefined {
  const upper = name.toUpperCase();
  return ORACLE_FUNCTIONS.find((fn) => fn.name === upper);
}
